import React, { useState } from 'react'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { HeadingMD, ParagraphLG } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { AngelPlayer } from '@/organisms/AngelPlayer'
import { IntroMeta } from '@/services/TorchesService/types'
import { useIntroAsset } from '@/utils/TorchGuildUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'

interface TorchesHeroBanner {
  heroMeta?: IntroMeta
}

export const TorchesHeroBanner: React.FC<TorchesHeroBanner> = ({ heroMeta }) => {
  const [isTorchModalOpen, setIsTorchModalOpen] = useState(false)
  const { innerWidth } = useWindowSize()

  const { t } = useTranslate('torches')
  const watchTorches = t('watchTorches', 'Watch Torches.')
  const supportNewProjects = t('supportNewProjects', 'Support New Projects.')
  const torchIntroParagraph = t(
    'torchIntroParagraph',
    "A torch is a short film prototype used to demonstrate a filmmaker's ability to produce, direct and tell a captivating story.",
  )
  const learnMoreButton = t('learnMoreAboutTorches', 'Learn more about torches')
  const name = t('torchIntroName', 'Torch Intro Video')
  const subtitle = t('torchIntroSubTitle', 'Help new torches come to life')

  const asset = useIntroAsset({ heroMeta, name, subtitle, slug: 'torches', videoType: 'torch' })

  return (
    <div className="relative">
      <ResponsiveBackgroundImage
        src="https://images.angelstudios.com/image/upload/w_412,h_790,c_thumb,g_auto/v1699022164/angel-funding/hero"
        srcUrls={{
          xs: 'https://images.angelstudios.com/image/upload/v1699022164/angel-funding/hero',
          sm: 'https://images.angelstudios.com/image/upload/v1699022164/angel-funding/hero',
          md: 'https://images.angelstudios.com/image/upload/v1699022164/angel-funding/hero',
          lg: 'https://images.angelstudios.com/image/upload/v1699022164/angel-funding/hero',
          xl: 'https://images.angelstudios.com/image/upload/v1699022165/angel-funding/hero_desktop',
          '2xl': 'https://images.angelstudios.com/image/upload/v1699022165/angel-funding/hero_desktop',
        }}
        backgroundSize={innerWidth >= 1280 ? 'bg-contain' : 'bg-cover'}
        className="-z-10 xl:bg-right"
      />
      <div className="w-screen max-w-[1728px] p-6 md:aspect-h-9 md:aspect-w-16 xl:aspect-none xl:px-12 2xl:px-[4.5rem]">
        <div className="mx-auto flex h-[70vh] w-full max-w-xl flex-col justify-center text-center text-white md:h-auto xl:mx-0 xl:h-[680px] xl:items-start xl:text-left">
          <HeadingMD weight="bold" className="md:photon-heading-lg lg:photon-heading-xl mb-4 md:mb-6 ">
            {watchTorches}
            <br />
            {supportNewProjects}
          </HeadingMD>
          <ParagraphLG className="lg:photon-paragraph-xl mb-8 md:mb-6">{torchIntroParagraph}</ParagraphLG>
          <Button
            variant="plain"
            className="flex items-center gap-1.5 self-center py-4 text-lg xl:self-start"
            onClick={() => setIsTorchModalOpen(true)}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full border-[3px] border-white">
              <PlayIcon className="ml-0.5" />
            </span>
            {learnMoreButton}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isTorchModalOpen}
        onClose={() => setIsTorchModalOpen(false)}
        panelClassesOverride="relative inline-block w-full max-w-screen-xl py-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
      >
        <Button className="absolute right-0 top-0 p-2" variant="plain" onClick={() => setIsTorchModalOpen(false)}>
          <CloseIcon size={20} />
        </Button>
        <AngelPlayer asset={asset} autoplay />
      </Modal>
    </div>
  )
}
